import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

// ─── LOGO PATH — same as other files ────────────────────────────────────────
const logoPath = (shortName) => `/assets/assets/${shortName?.toLowerCase()}.png`;
// ─────────────────────────────────────────────────────────────────────────────

function TeamLogo({ shortName, size = 32 }) {
  return (
    <img
      src={logoPath(shortName)}
      alt={shortName || ''}
      width={size} height={size}
      style={{ objectFit: 'contain', display: 'block' }}
      onError={e => { e.target.style.opacity = '0'; }}
    />
  );
}

const WA_SVG = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ─── WhatsApp Number ─────────────────────────────────────────────────────────
const WA_NUMBER = '917003093523';
// ─────────────────────────────────────────────────────────────────────────────

export default function CheckoutPage() {
  const { bookingId } = useParams();
  const { user }      = useAuth();
  const navigate      = useNavigate();
  const [booking,  setBooking]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [timeLeft, setTimeLeft] = useState(600);



  useEffect(() => {
    bookingsAPI.getById(bookingId)
      .then(r => setBooking(r.data))
      .catch(() => toast.error('Booking not found'))
      .finally(() => setLoading(false));
  }, [bookingId]);

  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft(p => {
        if (p <= 1) { clearInterval(t); toast.error('Session expired'); navigate(-1); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [navigate]);

  const mm = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const handleCancel = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    await bookingsAPI.cancel(bookingId).catch(() => {});
    navigate('/matches');
  };

  const waURL = () => {
    if (!booking) return '#';
    const msg =
`🏏 *IPL 2026 – Ticket Booking Confirmation*

👤 *Name:* ${user?.name || ''}
📧 *Email:* ${user?.email || ''}

🎟️ *Booking Details*
━━━━━━━━━━━━━━━━━━
🆔 ID: #${booking.bookingId}
🏟️ Match: ${booking.match?.team1?.shortName} vs ${booking.match?.team2?.shortName}
📍 Venue: ${booking.match?.venue?.name}, ${booking.match?.venue?.city}
📅 Date: ${booking.match?.date ? format(new Date(booking.match.date), 'dd MMM yyyy') : ''}
🕐 Time: ${booking.match?.time || ''}
🎫 Stand: ${booking.seats?.[0]?.category || ''}
🪑 Seats: ${booking.seats?.map(s => s.seatNumber).join(', ')}
🎟️ Qty: ${booking.seats?.length}
💰 Total: ₹${booking.grandTotal?.toLocaleString()}
━━━━━━━━━━━━━━━━━━

✅ Kindly confirm my booking and share payment details.`;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#09090f' }}>
      <div style={{ width: 42, height: 42, borderRadius: '50%', border: '3px solid rgba(255,107,0,0.15)', borderTopColor: '#ff6b00', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!booking) return (
    <div style={{ background: '#09090f', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
      <div style={{ fontSize: 42 }}>😕</div>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Rajdhani, sans-serif', fontSize: 15 }}>Booking not found</p>
      <button onClick={() => navigate('/matches')} style={{ padding: '10px 24px', background: '#ff6b00', color: '#fff', border: 'none', borderRadius: 9, fontFamily: 'Rajdhani, sans-serif', fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer' }}>Browse Matches</button>
    </div>
  );

  const { match, seats, totalAmount, convenienceFee, grandTotal } = booking;
  const urgent = timeLeft < 120;
  const c1 = match?.team1?.color || '#ff6b00';
  const c2 = match?.team2?.color || '#ffd700';

  return (
    <div style={{ background: '#09090f', minHeight: '100vh', paddingTop: 'var(--nav-h, 68px)', paddingBottom: 60, color: '#f0f0f8', fontFamily: 'Outfit, sans-serif' }}>
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)} }
        @keyframes pulse  { 0%,100%{opacity:1}50%{opacity:0.45} }
        .wa-cta:hover   { transform: translateY(-2px) !important; box-shadow: 0 12px 36px rgba(37,211,102,0.5) !important; }
        .ghost-btn:hover{ background: rgba(255,255,255,0.07) !important; }
        @media(max-width:768px){
          .checkout-grid { flex-direction: column !important; }
          .price-col     { order: -1; position: static !important; }
        }
      `}</style>

      {/* top match color strip */}
      <div style={{ height: 2, background: `linear-gradient(90deg, ${c1} 0%, #1a1a30 45%, ${c2} 100%)` }} />

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
          <div>
            <span style={{ display: 'inline-block', marginBottom: 8, background: 'rgba(255,107,0,0.1)', color: '#ff6b00', border: '1px solid rgba(255,107,0,0.22)', padding: '2px 12px', borderRadius: 20, fontFamily: 'Rajdhani, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em' }}>
              STEP 3 OF 3
            </span>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 'clamp(1.8rem,5vw,2.8rem)', letterSpacing: '0.04em', lineHeight: 1 }}>CHECKOUT</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.38)', fontFamily: 'Rajdhani, sans-serif', marginTop: 5 }}>
              Confirm your booking via WhatsApp
            </div>
          </div>

          {/* Timer */}
          <div style={{ padding: '12px 20px', borderRadius: 12, textAlign: 'center', background: urgent ? 'rgba(230,57,70,0.08)' : 'rgba(255,107,0,0.06)', border: `1px solid ${urgent ? 'rgba(230,57,70,0.28)' : 'rgba(255,107,0,0.18)'}` }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 3 }}>Expires in</div>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 32, lineHeight: 1, color: urgent ? '#e63946' : '#ff6b00', animation: urgent ? 'pulse 0.9s ease-in-out infinite' : 'none' }}>
              {mm(timeLeft)}
            </div>
          </div>
        </div>

        {/* ── Grid ── */}
        <div className="checkout-grid" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

          {/* LEFT — details */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Order summary */}
            <div style={{ background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '13px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Order Summary</div>
              </div>
              <div style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${c1}18`, border: `1.5px solid ${c1}3a`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TeamLogo shortName={match?.team1?.shortName} size={28} />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 22, letterSpacing: '0.04em' }}>
                        <span style={{ color: c1 }}>{match?.team1?.shortName}</span>
                        <span style={{ color: '#ff6b00', margin: '0 8px' }}>VS</span>
                        <span style={{ color: c2 }}>{match?.team2?.shortName}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontFamily: 'Rajdhani, sans-serif', marginTop: 2 }}>
                        {match?.venue?.name} · {match?.date ? format(new Date(match.date), 'dd MMM yyyy') : ''} · {match?.time}
                      </div>
                    </div>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `${c2}18`, border: `1.5px solid ${c2}3a`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TeamLogo shortName={match?.team2?.shortName} size={28} />
                    </div>
                  </div>
                  <span style={{ background: 'rgba(255,107,0,0.1)', color: '#ff6b00', border: '1px solid rgba(255,107,0,0.22)', padding: '2px 10px', borderRadius: 20, fontFamily: 'Rajdhani, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>
                    Match {match?.matchNumber}
                  </span>
                </div>
                {/* Seats */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {seats?.map(s => (
                    <div key={s.seatNumber} style={{ padding: '4px 13px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 8, fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>
                      {s.seatNumber}
                      <span style={{ marginLeft: 5, fontSize: 11, color: 'rgba(255,255,255,0.28)', fontWeight: 400 }}>({s.category})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* WhatsApp payment */}
            <div style={{ background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '13px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Complete Payment</div>
              </div>
              <div style={{ padding: '20px' }}>

                {/* Notice */}
                <div style={{ display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 18, background: 'rgba(37,211,102,0.05)', border: '1px solid rgba(37,211,102,0.14)', borderRadius: 10 }}>
                  <div style={{ color: '#25d366', marginTop: 1 }}>{WA_SVG}</div>
                  <div>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 3 }}>Pay via WhatsApp</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.42)', lineHeight: 1.55 }}>
                      Online payment is temporarily unavailable. Contact us on WhatsApp with your Booking ID — seats confirmed within minutes.
                    </div>
                  </div>
                </div>

                {/* Booking ID */}
                <div style={{ padding: '14px 18px', marginBottom: 20, background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.18)', borderRadius: 10 }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 5 }}>Your Booking ID</div>
                  <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 28, color: '#ff6b00', letterSpacing: '0.1em', lineHeight: 1 }}>{booking.bookingId}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontFamily: 'Rajdhani, sans-serif', marginTop: 4 }}>Share this on WhatsApp to confirm your booking</div>
                </div>

                {/* Steps */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    'Tap the WhatsApp button — your booking details are pre-filled',
                    'Pay via UPI or bank transfer as instructed by our team',
                    'Receive your confirmed e-ticket instantly after payment',
                    'Show e-ticket QR at the stadium gate on match day',
                  ].map((text, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Bebas Neue, sans-serif', fontSize: 13, color: '#25d366', flexShrink: 0 }}>
                        {i + 1}
                      </div>
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, paddingTop: 4 }}>{text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Price + CTA */}
          <div className="price-col" style={{ width: 300, flexShrink: 0, position: 'sticky', top: 88 }}>
            <div style={{ background: '#0f0f1e', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', animation: 'fadeUp 0.3s ease' }}>
              <div style={{ padding: '13px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>Price Breakdown</div>
              </div>
              <div style={{ padding: '18px 20px' }}>

                {/* Per seat */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                  {seats?.map(s => (
                    <div key={s.seatNumber} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'rgba(255,255,255,0.5)', fontFamily: 'Rajdhani, sans-serif' }}>
                      <span>{s.seatNumber} <span style={{ color: 'rgba(255,255,255,0.28)', fontSize: 11 }}>({s.category})</span></span>
                      <span>₹{s.price?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>

                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 0 12px' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 13, color: 'rgba(255,255,255,0.5)', fontFamily: 'Rajdhani, sans-serif' }}>
                  <span>Subtotal</span><span>₹{totalAmount?.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif' }}>
                  <span>Convenience (2%)</span><span>₹{convenienceFee?.toLocaleString()}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 20, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.65)' }}>Grand Total</span>
                  <span style={{ color: '#ffd700' }}>₹{grandTotal?.toLocaleString()}</span>
                </div>

                {/* WhatsApp button */}
                <a
                  href={waURL()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wa-cta"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px', borderRadius: 10, textDecoration: 'none', background: '#25d366', color: '#fff', fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '0.08em', textTransform: 'uppercase', boxShadow: '0 6px 22px rgba(37,211,102,0.35)', transition: 'all 0.18s' }}
                >
                  {WA_SVG} Confirm on WhatsApp
                </a>

                <button
                  className="ghost-btn"
                  onClick={handleCancel}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: 10, padding: '11px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)', background: 'transparent', color: 'rgba(255,255,255,0.3)', fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: 13, letterSpacing: '0.06em', cursor: 'pointer', transition: 'background 0.15s' }}
                >
                  Cancel Booking
                </button>

                <div style={{ marginTop: 14, textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.1em', marginBottom: 3 }}>BOOKING ID</div>
                  <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 16, color: '#ff6b00', letterSpacing: '0.1em' }}>{booking.bookingId}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}